import { useRef, useState } from 'react'
import type { FC } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Link as MLink,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { format, parseISO } from 'date-fns'
import { ISO_DATE_FORMAT, formatRFC3339DateTimeOrEmpty } from '~/shared/lib/date'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router'

import type { PatientUser } from '~/shared/types'
import { isCaseTodo, type RawTodoItem, type RawTodoResult } from './PatientToDoService'
import { queueNotification } from '~/shared/notifications/actions'
import { getUserName } from '~/shared/lib/names'
import { getUpdatedAtLabel } from '~/shared/lib/dateLabels'
import { useGroupOptions, UserGroupSelect } from '~/entities/assignee-group'
import { useManageSelectedTodo } from '~/features/member-profile/communication'
import { TaskEditor, useAddTask, useUpdateTask, type TaskFormDraft } from '~/entities/task'
import {
  CheckBox,
  PriorityIcon,
  PriorityToggleInline as PriorityToggle,
  SectionCard,
} from '~/shared/ui'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { NextActionDate } from '~/entities/work-unit'
import { haveChanged } from '~/shared/lib/haveChanged'
import { logger } from '~/shared/lib/logger'
const MAX_MESSAGE_LENGTH_INCLUDED_IN_FORWARD = 40

// The design's task grid: Task (check + priority + title) / Assigned to /
// Due / Created. Shared by the header row and every task row.
const TASK_GRID = {
  display: 'grid',
  // Fixed end tracks: header and rows are separate grid containers, so
  // content-sized tracks would resolve differently per row and misalign
  // the column headings.
  gridTemplateColumns: 'minmax(0, 1.7fr) minmax(0, 1fr) 11rem 16rem',
  gap: 1.25,
  alignItems: 'center',
} as const

/**
 * Design-refresh sibling of ToDoTask: the same interactions (complete toggle
 * with optimistic state, priority menu, inline title edit, assignee, due date,
 * view-original-message) re-rendered as a grid row.
 */
const ToDoTaskRefresh: FC<{
  contentType: RawTodoResult['content_type']
  todoItem: RawTodoItem['content_object']
  patient: PatientUser
  isReadOnly?: boolean
  setDisableEdit: (value: boolean) => void
  setIsTodoTaskUpdating: (value: boolean) => void
}> = ({ contentType, todoItem, patient, isReadOnly, setDisableEdit, setIsTodoTaskUpdating }) => {
  const [optimisticIsComplete, setOptimisticIsComplete] = useState<boolean | null>(null)

  const { mutateAsync: handleUpdateTask, isPending: isUpdatingTask } = useUpdateTask()
  const { mutateAsync: handleUpdateTaskAssignee, isPending: isUpdatingTaskAssignee } =
    useUpdateTask()

  const user = useSelector(state => {
    if (todoItem?.created_by) return state.users.byId[todoItem?.created_by]
    else return null
  })

  const [priorityChange, setPriorityChange] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)

  // Task relations are included on each todo item when requested via API but
  // are not part of the generated item type yet.
  const relations =
    (todoItem as { relations?: Array<{ content_type: string; object_id: number }> }).relations ?? []
  const messageRelation = relations.find(r => r.content_type === 'chatmessagev2')
  const hasMessageLink = Boolean(messageRelation)

  const navigate = useNavigate()
  const location = useLocation()

  const handleViewOriginalMessage = (e: React.MouseEvent) => {
    e.stopPropagation()
    const msgId = messageRelation?.object_id
    if (!msgId) return
    const params = new URLSearchParams(location.search)
    params.delete('caseId')
    params.delete('selectPayload')
    params.set('scrollToMessageId', String(msgId))
    params.set('clearChatSearch', '1')
    params.delete('search')
    void navigate({ pathname: location.pathname, search: params.toString() })
  }

  const toggleTaskComplete = async (item: RawTodoItem['content_object']) => {
    setDisableEdit(true)
    setIsTodoTaskUpdating(true)
    setOptimisticIsComplete(!item.is_complete)
    try {
      await handleUpdateTask({
        id: item.id,
        is_complete: !item.is_complete,
      })
    } finally {
      setOptimisticIsComplete(null)
      setDisableEdit(false)
      setIsTodoTaskUpdating(false)
    }
  }

  const onTaskUpdate = async (
    updatedField: 'title' | 'priority' | 'owner_group' | 'due_date',
    updatedFieldValue: string | number | null,
    item: RawTodoItem['content_object'],
  ) => {
    setIsTodoTaskUpdating(true)
    try {
      if (updatedField === 'owner_group') {
        await handleUpdateTaskAssignee({
          id: item.id,
          owner_group: updatedFieldValue as number,
        })
      } else {
        await handleUpdateTask({
          id: item.id,
          [updatedField]: updatedFieldValue,
        })
        setPriorityChange(false)
      }
    } finally {
      setIsTodoTaskUpdating(false)
    }
  }

  const { selectedCaseOwnerGroupOption, flatOptions } = useGroupOptions(
    todoItem?.owner_group ?? null,
    contentType !== 'case',
    patient,
    patient.person.care_team,
    todoItem.is_complete || isReadOnly,
  )

  const isComplete = optimisticIsComplete ?? todoItem.is_complete

  return (
    <Box
      sx={theme => ({
        ...TASK_GRID,
        py: 0.75,
        borderTop: `1px solid ${theme.palette.divider}`,
        ...(isUpdatingTask ? { opacity: 0.5, pointerEvents: 'none' } : {}),
      })}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          minWidth: 0,
          // Center the reused icon controls: svg baselines and PriorityIcon's
          // legacy inline marginBottom (-0.5rem, a table-era alignment hack)
          // otherwise sit the flag lower than the checkbox.
          '& svg': { display: 'block', marginBottom: '0 !important' },
          '& button': { display: 'inline-flex', alignItems: 'center' },
        }}
      >
        <CheckBox
          value={isComplete}
          onChecked={() =>
            fireAndForget('[ToDoTasks] toggle complete', () => toggleTaskComplete(todoItem))
          }
          disabled={isReadOnly}
        />
        {!priorityChange ? (
          <PriorityToggle
            priority={todoItem.priority ?? 0}
            onChange={(e, _) => {
              setAnchorEl(e.currentTarget)
            }}
            disabled={isUpdatingTask || todoItem.is_complete}
          />
        ) : (
          <CircularProgress variant="indeterminate" color="primary" size="1.8rem" />
        )}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          slotProps={{ list: { disablePadding: true } }}
        >
          {(
            [
              [0, 'Standard'],
              [1, 'High'],
              [2, 'Urgent'],
            ] as const
          ).map(([level, label]) => (
            <MenuItem
              key={level}
              onClick={() => {
                setPriorityChange(true)
                setAnchorEl(null)
                fireAndForget('[ToDoTasks] update priority', () =>
                  onTaskUpdate('priority', level, todoItem),
                )
              }}
            >
              <PriorityIcon level={level} variant="view" />
              <Typography sx={{ ml: 1 }}>{label}</Typography>
            </MenuItem>
          ))}
        </Menu>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <TextField
            hiddenLabel
            disabled={isReadOnly || todoItem.is_complete}
            placeholder={'+ Add title'}
            defaultValue={todoItem.title}
            multiline
            onClick={e => {
              e.stopPropagation()
            }}
            onBlur={e => {
              if (e.target.value !== '' && todoItem.title !== e.target.value) {
                fireAndForget('[ToDoTasks] update title', () =>
                  onTaskUpdate('title', e.target.value, todoItem),
                )
              }
            }}
            sx={theme => ({
              width: '100%',
              '& .MuiInputBase-root.MuiOutlinedInput-root': { padding: 0 },
              '& textarea': {
                padding: '2px 0 2px 4px',
                fontSize: '1.3rem',
                color: isComplete ? theme.palette.text.secondary : theme.palette.text.primary,
              },
              '& fieldset': { border: 'none' },
              '& textarea:focus': {
                border: `1px solid ${theme.palette.grey[500]}`,
                borderRadius: '4px',
              },
            })}
          />
          {hasMessageLink && (
            <MLink
              data-testid="view-original-message-link"
              onClick={handleViewOriginalMessage}
              underline="always"
              color="secondary"
              sx={{ alignSelf: 'flex-start', ml: 0.5, fontSize: '1.2rem', cursor: 'pointer' }}
            >
              View original message
            </MLink>
          )}
        </Box>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <UserGroupSelect
          selectedValue={selectedCaseOwnerGroupOption ?? null}
          options={flatOptions}
          onChange={ownerGroupOption =>
            fireAndForget('[ToDoTasks] update owner group', () =>
              onTaskUpdate('owner_group', ownerGroupOption.value, todoItem),
            )
          }
          detailed
          // Fill the grid cell: at its default fixed 180px the control overhangs
          // the Due column once the track shrinks, and its transparent input
          // swallows clicks meant for the due date.
          fullWidth
          patient={patient}
          disabled={todoItem.is_complete || isReadOnly || isUpdatingTaskAssignee}
        />
      </Box>
      <NextActionDate
        date={todoItem.due_date}
        editable={!todoItem.is_complete}
        newTodos={true}
        onChange={dueDate =>
          fireAndForget('[ToDoTasks] update due date', () =>
            onTaskUpdate('due_date', dueDate, todoItem),
          )
        }
        // The Due track positions this cell, so drop the component's own
        // offsets: they would push the date off its column heading and past
        // the track into Created. The date input is sized to the track for the
        // same reason — its intrinsic width is wider than the column.
        sx={{ marginLeft: 0, minWidth: 0, '& .MuiFormControl-root': { width: '100%' } }}
      />
      <Box sx={{ textAlign: 'right' }}>
        {user ? (
          <>
            <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
              Created by {getUserName(user)}
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
              {todoItem.created_at ? getUpdatedAtLabel(parseISO(todoItem.created_at)) : ''}
            </Typography>
          </>
        ) : null}
      </Box>
    </Box>
  )
}

/**
 * Design-refresh sibling of ToDoTasks: the mock's task grid (uppercase column
 * labels, divider rows) with a slim "+ Add task" affordance and a
 * "Show N completed tasks" link — same mutations, forward-draft handling,
 * and AddTaskEditor as the legacy list.
 */
const ToDoTasks = (props: {
  parentId: number
  patient: PatientUser
  todoContent: RawTodoResult
  isClosed: boolean
  setDisableEdit: (value: boolean) => void
  setIsTodoTaskUpdating: (value: boolean) => void
}) => {
  const { parentId, patient, todoContent, isClosed, setDisableEdit, setIsTodoTaskUpdating } = props
  const me = useSelector(state => state.me)
  const dispatch = useDispatch()

  const newTaskData = {
    title: '',
    due_date: format(new Date(), ISO_DATE_FORMAT),
    ...{ owner_group: me.assignee_group?.id },
    priority: 0,
    is_complete: false,
  }

  const [addingTask, setAddingTask] = useState<boolean>(false)
  const [newTask, setNewTask] = useState<TaskFormDraft>(newTaskData)
  const [showCompleted, setShowCompleted] = useState<boolean>(false)
  const editorRef = useRef<HTMLDivElement | null>(null)

  const { isPending: isLoading, mutateAsync: handleCreateTask } = useAddTask()

  const toDoItems = todoContent.items.map(item => item.content_object)
  const contentType = todoContent.content_type
  const parentOwnerGroup = isCaseTodo(todoContent)
    ? todoContent.content_object.owner_group
    : undefined

  const showExpandLink: boolean = todoContent.items.some(item => item.content_object.is_complete)
  const numCompleted = todoContent.items.filter(item => item.content_object.is_complete).length

  const { selectedCaseId, forwardDraftCaseId, selectPayload, resetToCase } = useManageSelectedTodo()
  const createForwardPayload = selectPayload?.type === 'createForwardTask' ? selectPayload : null

  const handleAddTask = async () => {
    const buildTaskRelations = (
      task: TaskFormDraft,
      payload: { type?: string; message?: { id: number } } | null,
      relationContentType: string,
      relationParentId: number,
    ) => {
      if (task.is_forwarding_message && payload?.type === 'createForwardTask' && payload.message) {
        return [
          {
            content_type: 'chatmessagev2',
            object_id: payload.message.id,
            is_parent: false,
          },
          {
            content_type: relationContentType,
            object_id: relationParentId,
            is_parent: true,
          },
        ]
      }

      return [
        {
          content_type: relationContentType,
          object_id: relationParentId,
        },
      ]
    }

    const relations = buildTaskRelations(newTask, createForwardPayload, contentType, parentId)
    const payload = {
      title: newTask.title,
      due_date: formatRFC3339DateTimeOrEmpty(newTask.due_date),
      owner_group: newTask.owner_group,
      priority: newTask.priority,
      is_forwarding_message: newTask.is_forwarding_message,
      patient: patient.id,
      person: patient.person.id,
      relations,
      is_complete: false,
    }
    setIsTodoTaskUpdating(true)
    try {
      await handleCreateTask(payload)
      dispatch(queueNotification({ variant: 'success', message: 'Task created' }))
      setAddingTask(false)
      resetToCase(selectedCaseId)
      setNewTask(newTaskData)
    } catch (e) {
      logger.error('[ToDoTasks] add task', e)
      dispatch(queueNotification({ variant: 'error', message: 'Failed to add task' }))
    } finally {
      setIsTodoTaskUpdating(false)
    }
  }

  // Open the editor when a chat message is forwarded onto this case. Only primitive projections
  // of the payload are compared, so re-parsing the URL can't reopen it.
  const forwardType = createForwardPayload?.type
  const forwardOwnerGroup = createForwardPayload?.ownerGroup
  const forwardMessageText = createForwardPayload?.message?.text
  const forwardTrigger = [
    forwardDraftCaseId,
    parentId,
    forwardType,
    forwardOwnerGroup,
    forwardMessageText,
    parentOwnerGroup,
  ]
  const [lastForwardTrigger, setLastForwardTrigger] = useState<unknown[]>()
  const isNewForwardTrigger = haveChanged(lastForwardTrigger, forwardTrigger)
  if (isNewForwardTrigger) setLastForwardTrigger(forwardTrigger)
  if (isNewForwardTrigger && forwardDraftCaseId === parentId) {
    if (forwardType === 'createForwardTask') {
      let title = `RE: "${forwardMessageText || ''}`
      if (title.length >= MAX_MESSAGE_LENGTH_INCLUDED_IN_FORWARD) {
        const elipsis = '...'
        title =
          title.substring(0, MAX_MESSAGE_LENGTH_INCLUDED_IN_FORWARD - elipsis.length) + elipsis
      }
      title += '"'
      setNewTask(current => ({
        ...current,
        title,
        due_date: format(new Date(), ISO_DATE_FORMAT),
        owner_group: forwardOwnerGroup ?? parentOwnerGroup ?? current.owner_group,
        priority: 1,
        is_complete: false,
        is_forwarding_message: true,
      }))
    }
    setAddingTask(true)
  }

  const visibleItems = toDoItems.filter(item => {
    if (!showExpandLink) return true
    return !item.is_complete || showCompleted
  })

  return (
    <Box sx={{ px: 0.5 }}>
      {visibleItems.length > 0 && (
        <Box
          sx={{
            ...TASK_GRID,
            pb: 0.5,
            fontSize: '1.05rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'text.secondary',
          }}
        >
          <span>Task</span>
          <span>Assigned to</span>
          <span>Due</span>
          <span />
        </Box>
      )}
      {visibleItems.map(todoItem => (
        <ToDoTaskRefresh
          key={`ToDoTaskRefresh_${contentType}_${todoItem.id}`}
          todoItem={todoItem}
          patient={patient}
          contentType={contentType}
          // A closed WorkUnit's remaining incomplete tasks can still be completed.
          isReadOnly={isClosed && todoItem.is_complete}
          setDisableEdit={setDisableEdit}
          setIsTodoTaskUpdating={setIsTodoTaskUpdating}
        />
      ))}

      {!addingTask && contentType !== 'worklist' && (
        <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
          <Button
            variant="text"
            size="small"
            color={isClosed ? 'inherit' : 'primary'}
            disabled={isClosed}
            startIcon={<AddRoundedIcon />}
            onClick={() => setAddingTask(true)}
            sx={{ fontSize: '1.25rem', fontWeight: 600 }}
          >
            {isClosed ? 'Update status to add tasks' : 'Add task'}
          </Button>
          {showExpandLink && (
            <MLink
              component="button"
              onClick={() => setShowCompleted(!showCompleted)}
              color="secondary"
              underline="always"
              sx={{
                ml: 'auto',
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                cursor: 'pointer',
              }}
            >
              {showCompleted
                ? `Hide ${numCompleted} completed tasks`
                : `Show ${numCompleted} completed tasks`}
            </MLink>
          )}
        </Box>
      )}
      {addingTask && (
        <Box ref={editorRef} sx={{ my: 1.5 }}>
          <SectionCard>
            <TaskEditor
              disabled={isLoading}
              task={newTask}
              patient={patient}
              onChange={task => {
                setNewTask(task)
              }}
              canAssignToPatient={contentType === 'case' ? false : true}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                disabled={isLoading}
                onClick={() => {
                  setAddingTask(false)
                  resetToCase(selectedCaseId)
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={isLoading || !newTask.title}
                variant="contained"
                color="primary"
                onClick={() => {
                  void handleAddTask()
                }}
              >
                Add Task
              </Button>
            </Box>
          </SectionCard>
        </Box>
      )}
    </Box>
  )
}

export default ToDoTasks
